import { computed, ref } from 'vue';
import type { ProfileSample } from '../../utils/recordingActivity/profiles';
import {
  defaultSplitStep,
  longerSplitStep,
  paceSplitsOf,
  shorterSplitStep,
  type PaceSplitTrack,
} from '../../utils/recordingActivity/paceSplits';

/**
 * The split length and the picked split, shared by the splits panel and All splits so each opens
 * where the other was left. A new length picks its fastest split again.
 */
export function usePaceSplits(track: PaceSplitTrack, heartRate: ProfileSample[] | null, metersPerUnit: number) {
  const totalMeters = track.distances[track.distances.length - 1];
  const step = ref(defaultSplitStep(totalMeters, metersPerUnit));
  const picked = ref<number | null>(null);

  const splits = computed(() => paceSplitsOf(track, step.value * metersPerUnit, heartRate));
  const selected = computed(() => picked.value ?? splits.value?.fastestIndex ?? 0);
  const shorterStep = computed(() => shorterSplitStep(step.value, totalMeters, metersPerUnit));
  const longerStep = computed(() => longerSplitStep(step.value, totalMeters, metersPerUnit));

  function stepTo(next: number) {
    step.value = next;
    picked.value = null;
  }

  function select(index: number) {
    picked.value = index;
  }

  return { step, splits, selected, shorterStep, longerStep, hasHeartRate: heartRate !== null, stepTo, select };
}

export type PaceSplitsState = ReturnType<typeof usePaceSplits>;
